import { Box, type BoxProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useVisibleViewportInsets } from "@/contexts/VisibleViewportContext";
import LoadingIndicator from "./LoadingIndicator";

export type LoadingStateProps = Omit<BoxProps, "children"> & {
  compact?: boolean;
  label?: string;
  placement?: "content" | "viewport";
};

const LoadingState = ({
  compact = false,
  label = "A carregar…",
  placement = "content",
  sx,
  ...boxProps
}: LoadingStateProps) => {
  const { top, bottom } = useVisibleViewportInsets();
  const viewportStyles: SxProps<Theme> =
    placement === "viewport"
      ? {
          position: "fixed" as const,
          top,
          right: 0,
          bottom,
          left: 0,
          zIndex: 1,
          backgroundColor: theme => theme.palette.background.default,
        }
      : {
          width: "100%",
          minHeight: theme => theme.spacing(compact ? 8 : 15),
        };

  return (
    <Box
      {...boxProps}
      role="status"
      aria-live="polite"
      aria-label={label}
      data-testid="loading-state"
      sx={[
        {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        viewportStyles,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <LoadingIndicator compact={compact} label={label} />
    </Box>
  );
};

export default LoadingState;
